import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatApiError } from "@/lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const GRADES = ["أول ثانوي", "ثاني ثانوي", "ثالث ثانوي", "غير محدد"];
const FIELDS = [
    { key: "full_name", label: "اسم الطالبة", required: true },
    { key: "national_id", label: "رقم الهوية", required: true },
    { key: "section", label: "الفصل / الشعبة", required: false },
];

export default function ExcelImport() {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({ full_name: "", national_id: "", section: "" });
    const [grade, setGrade] = useState("ثاني ثانوي");
    const [defaultSection, setDefaultSection] = useState("أ");
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const doPreview = async (f) => {
        setErr(""); setReport(null); setPreview(null);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/import/preview", fd);
            setPreview(data);
            setMapping({
                full_name: data.suggested_mapping.full_name || "",
                national_id: data.suggested_mapping.national_id || "",
                section: data.suggested_mapping.section || "",
            });
            setGrade(data.grade && data.grade !== "غير محدد" ? data.grade : "ثاني ثانوي");
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const onSelectFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        doPreview(f);
    };

    const doImport = async () => {
        if (!file) return;
        if (!mapping.full_name || !mapping.national_id) return setErr("الرجاء اختيار عمود الاسم ورقم الهوية");
        setBusy(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("grade", grade);
            fd.append("mapping_json", JSON.stringify(mapping));
            fd.append("default_section", defaultSection);
            const { data } = await api.post("/admin/import/execute", fd);
            setReport(data);
            toast.success(`تم استيراد ${data.added} طالبة`);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const reset = () => {
        setFile(null); setPreview(null); setReport(null); setErr("");
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <div className="space-y-6 max-w-6xl">
            <div>
                <h1 className="text-2xl font-bold text-[#0F172A]">استيراد ملفات Excel</h1>
                <p className="text-sm text-slate-500 mt-1">ارفعي ملف الطالبات لإنشاء الحسابات تلقائياً</p>
            </div>

            {!preview && !report && (
                <div
                    data-testid="excel-upload-dropzone"
                    onClick={() => fileRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-xl bg-white p-12 text-center cursor-pointer hover:border-[#1B2A4E] hover:bg-slate-50 transition-all"
                >
                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                        <Upload className="w-6 h-6 text-[#1B2A4E]" />
                    </div>
                    <div className="text-[#0F172A] font-semibold">اسحبي ملف Excel هنا أو انقري للاختيار</div>
                    <div className="text-xs text-slate-500 mt-1">صيغ مدعومة: .xlsx, .xls</div>
                    <input ref={fileRef} data-testid="excel-file-input" type="file" accept=".xlsx,.xls" hidden onChange={onSelectFile} />
                </div>
            )}

            {err && <Alert variant="destructive"><AlertDescription data-testid="import-error">{err}</AlertDescription></Alert>}

            {preview && !report && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5" data-testid="mapping-panel">
                    <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                        <div className="flex-1">
                            <div className="font-semibold text-[#0F172A]">{preview.filename}</div>
                            <div className="text-xs text-slate-500">تم اكتشاف {preview.total_rows} صف • {preview.columns.length} عمود</div>
                        </div>
                        <Button variant="outline" onClick={reset} data-testid="btn-cancel-import">إلغاء</Button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label>الصف الدراسي</Label>
                            <Select value={grade} onValueChange={setGrade}>
                                <SelectTrigger data-testid="grade-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>الفصل الافتراضي (لو لم يُذكر في الملف)</Label>
                            <Input value={defaultSection} onChange={(e) => setDefaultSection(e.target.value)} className="mt-1.5" data-testid="default-section-input" />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <h3 className="font-semibold text-[#0F172A] text-sm">مطابقة الأعمدة</h3>
                        {FIELDS.map((f) => (
                            <div key={f.key} className="grid md:grid-cols-3 gap-3 items-center">
                                <Label className="text-sm">{f.label} {f.required && <span className="text-[#C8102E]">*</span>}</Label>
                                <div className="md:col-span-2">
                                    <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "__none__" ? "" : v }))}>
                                        <SelectTrigger data-testid={`map-${f.key}`}><SelectValue placeholder="اختاري عموداً" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— لا شيء —</SelectItem>
                                            {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>{preview.columns.map((c) => <th key={c} className="px-3 py-2 text-right font-semibold">{c}</th>)}</tr>
                            </thead>
                            <tbody>
                                {preview.sample_rows.map((r, i) => (
                                    <tr key={i} className="border-t border-slate-100">
                                        {preview.columns.map((c) => <td key={c} className="px-3 py-2 text-slate-700">{r[c]}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end">
                        <Button onClick={doImport} disabled={busy} data-testid="excel-start-import-button" className="bg-[#1B2A4E] hover:bg-[#0F172A] h-11 px-8">
                            {busy ? "جارٍ الاستيراد..." : "تأكيد الاستيراد وإنشاء الحسابات"}
                        </Button>
                    </div>
                </div>
            )}

            {report && (
                <div className="space-y-4" data-testid="excel-import-report-card">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5" />
                        <div>
                            <div className="font-semibold text-emerald-900">تمّ الاستيراد بنجاح</div>
                            <div className="text-sm text-emerald-800">تم إنشاء الحسابات وربطها بالفصول تلقائياً</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                            ["تم إضافتهن", report.added, "text-[#1B2A4E]"],
                            ["حسابات الطالبات", report.students_created, "text-[#1B2A4E]"],
                            ["حسابات ولي الأمر", report.parents_created, "text-[#1B2A4E]"],
                            ["مكرر", report.duplicates, "text-amber-600"],
                            ["أخطاء", report.errors.length, "text-[#C8102E]"],
                        ].map(([label, val, cls], i) => (
                            <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                                <div className={`text-2xl font-bold font-mono-num ${cls}`}>{val}</div>
                                <div className="text-xs text-slate-500 mt-1">{label}</div>
                            </div>
                        ))}
                    </div>
                    {report.errors.length > 0 && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3 text-[#C8102E]"><AlertTriangle className="w-4 h-4" /><span className="font-semibold text-sm">سجلات لم يتم استيرادها</span></div>
                            <div className="max-h-64 overflow-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2 text-right">الصف</th><th className="px-3 py-2 text-right">الاسم</th><th className="px-3 py-2 text-right">السبب</th></tr></thead>
                                    <tbody>{report.errors.map((e, i) => (<tr key={i} className="border-t border-slate-100"><td className="px-3 py-2 font-mono-num">{e.row}</td><td className="px-3 py-2">{e.name}</td><td className="px-3 py-2 text-slate-600">{e.reason}</td></tr>))}</tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className="flex gap-3">
                        <Button onClick={reset} data-testid="btn-new-import" className="bg-[#1B2A4E] hover:bg-[#0F172A]">استيراد ملف آخر</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
